import {
  CementoAccessPoint,
  CementoCheckoutState,
  CementoMapaAddress,
  TIPO_ENTREGA_RETIRA_CLIENTE,
} from 'src/app/models/cemento-checkout';
import {
  cementProductActiveDeliveryOption,
  CementProduct,
} from 'src/app/models/cement-product';
import {
  CementoCotizacionRecoverResponse,
  CementoCotizacionSavePayload,
} from 'src/app/services/cemento-cotizacion.service';

function roundCoordinate(value: number): number {
  return Number(value.toFixed(7));
}

const SACOS_PER_PALLET = 72;

export function normalizeCementoTotalPrice(value: number | null | undefined): number {
  const total = Number(value);
  if (!Number.isFinite(total) || total <= 0) {
    return 0;
  }
  return Math.round(total);
}

export function calculateCementoCheckoutTotal(
  unitPrice: number | null | undefined,
  pallets: number | null | undefined,
  sacksPerPallet: number = SACOS_PER_PALLET
): number {
  const normalizedUnitPrice = Number(unitPrice);
  const normalizedPallets = Number(pallets);
  if (
    !Number.isFinite(normalizedUnitPrice) ||
    normalizedUnitPrice <= 0 ||
    !Number.isFinite(normalizedPallets) ||
    normalizedPallets <= 0
  ) {
    return 0;
  }

  return Math.round(normalizedUnitPrice) * Math.round(normalizedPallets) * sacksPerPallet;
}

/**
 * Pallets operativos para política/centro y UI: usa ``pallets`` si viene en estado;
 * si no, deriva desde sacos con ``multiplo``. Si ``quantity`` es menor que un
 * pallet completo (cotizaciones legadas donde ``quantity`` eran pallets), se
 * interpreta como conteo de pallets.
 */
export function resolveQuantityPalletsForPayload(
  product: CementProduct,
  cantidad: NonNullable<CementoCheckoutState['cantidadServicios']>
): number {
  const active = cementProductActiveDeliveryOption(product);
  const multiplo = Number(active?.multiplo ?? product.multiplo ?? 0);
  const rawPallets = cantidad.pallets;
  if (typeof rawPallets === 'number' && Number.isFinite(rawPallets) && rawPallets > 0) {
    return Math.round(rawPallets);
  }
  const sacks =
    typeof cantidad.quantity === 'number' && Number.isFinite(cantidad.quantity)
      ? cantidad.quantity
      : 0;
  if (sacks <= 0) {
    return 0;
  }
  if (multiplo > 0) {
    const derived = Math.round(sacks / multiplo);
    if (derived >= 1) {
      return derived;
    }
    return Math.round(sacks);
  }
  return Math.round(sacks);
}

function resolveAccessPointForPayload(
  mapa: NonNullable<CementoCheckoutState['mapa']>,
  dispatchAddress: CementoMapaAddress,
  accessPoint: CementoAccessPoint
): CementoAccessPoint {
  if (mapa.accessPointOptional && !accessPoint.address?.trim()) {
    const fallbackLine = `${dispatchAddress.street} ${dispatchAddress.number}, ${dispatchAddress.comuna}, ${dispatchAddress.region}`.trim();
    return {
      lat: dispatchAddress.lat,
      lng: dispatchAddress.lng,
      address: dispatchAddress.address?.trim() || fallbackLine,
      region: dispatchAddress.region,
      comuna: dispatchAddress.comuna,
      street: dispatchAddress.street,
      number: dispatchAddress.number,
      regionCode: accessPoint.regionCode,
    };
  }
  return accessPoint;
}

export function buildCementoQuotePayload(
  state: CementoCheckoutState,
  token: string
): CementoCotizacionSavePayload {
  const product = state.producto?.product;
  const billing = state.facturacion?.billing;
  const dispatchAddress = state.mapa?.dispatchAddress;
  const accessPointRaw = state.mapa?.accessPoint;
  const tipoEntrega = state.tipoEntrega;
  const cantidad = state.cantidadServicios;
  const fecha = state.fechaServicios?.schedule;

  if (
    !product ||
    !billing ||
    !state.mapa ||
    !dispatchAddress ||
    !accessPointRaw ||
    !tipoEntrega?.tipoEntrega ||
    !cantidad?.quantity ||
    !fecha?.date
  ) {
    throw new Error('No pudimos construir la cotización de cemento.');
  }

  const accessPoint = resolveAccessPointForPayload(
    state.mapa,
    dispatchAddress,
    accessPointRaw
  );
  const quantityPallets = resolveQuantityPalletsForPayload(product, cantidad);
  const normalizedTotalPrice =
    normalizeCementoTotalPrice(cantidad.totalPrice) ||
    calculateCementoCheckoutTotal(cantidad.unitPrice, quantityPallets);

  return {
    order_buy: state.orderBuy,
    resume_token: state.resumeToken,
    product_id: cementProductActiveDeliveryOption(product).id,
    incoterm: String(tipoEntrega.incoterm ?? product.selected_incoterm ?? product.incoterms ?? '').trim(),
    tipo_entrega: tipoEntrega.tipoEntrega,
    tipo_despacho: tipoEntrega.tipoDespacho,
    comuna_retiro:
      tipoEntrega.tipoEntrega === TIPO_ENTREGA_RETIRA_CLIENTE && tipoEntrega.comuna
        ? {
            id: tipoEntrega.comuna.id,
            code: tipoEntrega.comuna.code,
            name: tipoEntrega.comuna.name,
          }
        : undefined,
    quantity: cantidad.quantity,
    quantity_pallets: quantityPallets,
    unit_price: cantidad.unitPrice ?? 0,
    total_price: normalizedTotalPrice,
    lead_hours: cantidad.leadHours,
    schedule_date: fecha.date,
    billing: {
      email: billing.email ?? '',
      name: billing.name ?? '',
      rut: billing.rut ?? '',
      giro: (billing.giro ?? state.facturacion?.contactName ?? '').trim(),
      address: billing.address ?? '',
      phone: billing.phone ?? '',
    },
    contact: {
      name: state.facturacion?.contactName ?? '',
      phone: state.facturacion?.contactPhone ?? '',
      delivery_instructions: state.facturacion?.deliveryInstructions ?? '',
    },
    dispatch_address: {
      ...dispatchAddress,
      lat: roundCoordinate(dispatchAddress.lat),
      lng: roundCoordinate(dispatchAddress.lng),
      regionCode: dispatchAddress.regionCode ?? '',
    },
    access_point: {
      ...accessPoint,
      lat: roundCoordinate(accessPoint.lat),
      lng: roundCoordinate(accessPoint.lng),
      regionCode: accessPoint.regionCode ?? '',
    },
    same_dispatch_address: state.facturacion?.sameDispatchAddress ?? false,
    accept_terms: state.facturacion?.acceptTerms ?? false,
    auth_user_id: null,
    token,
  };
}

function normalizeCantidadServicios(
  c: CementoCheckoutState['cantidadServicios']
): CementoCheckoutState['cantidadServicios'] {
  if (!c) return c;
  const normalizedExistingTotal = normalizeCementoTotalPrice(c.totalPrice);
  if (normalizedExistingTotal > 0) {
    return { ...c, totalPrice: normalizedExistingTotal };
  }
  const pallets = typeof c.pallets === 'number' && c.pallets > 0 ? c.pallets : 0;
  const unitPrice = typeof c.unitPrice === 'number' && c.unitPrice > 0 ? c.unitPrice : 0;
  if (pallets <= 0 || unitPrice <= 0) return c;
  return { ...c, totalPrice: calculateCementoCheckoutTotal(unitPrice, pallets) };
}

export function mapRecoveredCementoQuoteToState(
  response: CementoCotizacionRecoverResponse
): CementoCheckoutState {
  const rawMapa = response.mapa as Record<string, any>;
  const mapa = rawMapa
    ? {
        ...rawMapa,
        dispatchAddress: rawMapa['dispatchAddress']
          ? {
              ...rawMapa['dispatchAddress'],
              regionCode: Number(rawMapa['dispatchAddress']['regionCode']),
            }
          : undefined,
        accessPoint: rawMapa['accessPoint']
          ? {
              ...rawMapa['accessPoint'],
              regionCode: Number(rawMapa['accessPoint']['regionCode']),
            }
          : undefined,
      }
    : undefined;
  return {
    orderBuy: response.order_buy,
    resumeToken: (response as CementoCotizacionRecoverResponse & { resume_token?: string }).resume_token,
    mapa: mapa as CementoCheckoutState['mapa'],
    producto: response.producto
      ? {
          ...(response.producto as CementoCheckoutState['producto']),
          product: (response.producto as CementoCheckoutState['producto'])?.product
            ? {
                ...(response.producto as CementoCheckoutState['producto']).product,
                selected_incoterm: String(
                  (response.tipoEntrega as CementoCheckoutState['tipoEntrega'])?.incoterm ?? ''
                ).trim().toUpperCase(),
              }
            : undefined,
        }
      : undefined,
    tipoEntrega: response.tipoEntrega as CementoCheckoutState['tipoEntrega'],
    cantidadServicios: normalizeCantidadServicios(
      response.cantidadServicios as CementoCheckoutState['cantidadServicios']
    ),
    fechaServicios: response.fechaServicios as CementoCheckoutState['fechaServicios'],
    facturacion: response.facturacion as CementoCheckoutState['facturacion'],
  };
}

export function findMatchingRecoveredProduct(
  products: CementProduct[],
  selected: CementProduct | undefined
): CementProduct | null {
  if (!selected) {
    return null;
  }
  const byId = products.find((item) => item.id === selected.id);
  if (byId) {
    const savedIncoterm = String(selected.selected_incoterm ?? '').trim().toUpperCase();
    if (savedIncoterm) {
      byId.selected_incoterm = savedIncoterm;
    }
    return byId;
  }

  for (const item of products) {
    const option = (item.delivery_options ?? []).find((opt) => opt.id === selected.id);
    if (option) {
      item.selected_incoterm = String(option.incoterms ?? '').trim().toUpperCase();
      return item;
    }
  }

  return products.find((item) => item.display_label === selected.display_label) ?? null;
}
